import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";

import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { PageLayout } from "../components/PageLayout";

import { getTemplates } from "../data/templates";

import type { TemplateMeta } from "../data/templates";

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);

  useEffect(() => {
    getTemplates()
      .then((data) => setTemplates(data))
      .catch((err) => console.error(err));
  }, []);

  return (
    <PageLayout>
      <div className="container py-8 min-h-[calc(100vh-10rem)]">
        <Tabs defaultValue="all" className="space-y-8">
          <div className="flex items-center justify-between">
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 max-w-2xl h-full">
              <TabsTrigger value="all">All Templates</TabsTrigger>
              <TabsTrigger value="flowcharts">Flowcharts</TabsTrigger>
              <TabsTrigger value="diagrams">Diagrams</TabsTrigger>
              <TabsTrigger value="mindmaps">Mind Maps</TabsTrigger>
              <TabsTrigger value="wireframes">Wireframes</TabsTrigger>
              <TabsTrigger value="charts">Charts</TabsTrigger>
            </TabsList>
            <div className="hidden md:flex gap-2">
              <Button variant="outline" size="sm">
                Most Popular
              </Button>
              <Button variant="outline" size="sm">
                Newest
              </Button>
            </div>
          </div>

          <TabsContent value="all" className="space-y-8">
            {/* Featured Templates */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Featured Templates</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates
                  .filter((template) => template.popular)
                  .slice(0, 3)
                  .map((template) => (
                    <TemplateCard key={template.id} template={template} />
                  ))}
              </div>
            </section>

            {/* New Templates */}
            <section>
              <h2 className="text-2xl font-bold mb-6">New Templates</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates
                  .filter((template) => template.new)
                  .slice(0, 3)
                  .map((template) => (
                    <TemplateCard key={template.id} template={template} />
                  ))}
              </div>
            </section>

            {/* All Templates */}
            <section>
              <h2 className="text-2xl font-bold mb-6">All Templates</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            </section>
          </TabsContent>

          <TabsContent value="flowcharts">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates
                .filter((template) => template.category === "flowcharts")
                .map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
            </div>
          </TabsContent>

          <TabsContent value="diagrams">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates
                .filter((template) => template.category === "diagrams")
                .map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
            </div>
          </TabsContent>

          <TabsContent value="mindmaps">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates
                .filter((template) => template.category === "mindmaps")
                .map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
            </div>
          </TabsContent>

          <TabsContent value="wireframes">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates
                .filter((template) => template.category === "wireframes")
                .map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
            </div>
          </TabsContent>

          <TabsContent value="charts">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates
                .filter((template) => template.category === "charts")
                .map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
}

function TemplateCard({ template }: { template: TemplateMeta }) {
  return (
    <div className="group relative overflow-hidden rounded-lg border bg-background transition-all hover:shadow-md md:hover:scale-[1.02]">
      <Link
        to={`/editor?template=${template.id}`}
        className="absolute inset-0 z-10"
      >
        <span className="sr-only">View {template.title}</span>
      </Link>
      <div className="relative aspect-video overflow-hidden bg-muted">
        <img
          src={template.image || "/placeholder.svg"}
          alt={template.title}
          className="object-cover transition-transform group-hover:scale-105"
        />
        {template.new && (
          <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground">
            New
          </Badge>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-medium">{template.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
          {template.description}
        </p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center text-sm text-muted-foreground">
            <Star className="h-3.5 w-3.5 fill-primary text-primary mr-1" />
            <span>{template.stars}</span>
          </div>
          <Button size="sm" className="z-20 relative">
            Use Template
          </Button>
        </div>
      </div>
    </div>
  );
}
